import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../lib/api'

export interface Department {
  id: string
  tenant_id: string
  name: string
  organization_id?: string | null
  parent_id?: string | null
  code_1c?: string | null
  manager_id?: string | null
  created_at: string
  employee_count?: number
  // Backend returns 'organizations' (Prisma relation name)
  organizations?: {
    id: string
    name: string
    bin?: string
  } | null
  departments?: Department | null
  other_departments?: Department[]
  employees_departments_manager_idToemployees?: {
    id: string
    first_name: string
    last_name: string
    employee_number: string
  } | null
}

export interface DepartmentFilters {
  search?: string
  organization_id?: string
  parent_id?: string
  is_root?: boolean
  page?: number
  limit?: number
  sort?: string
}

export interface PaginatedDepartments {
  data: Department[]
  total: number
  page: number
  limit: number
  totalPages: number
  from1cCount: number
  localCount: number
}

const DEPARTMENTS_QUERY_KEY = 'departments'

export const useDepartments = (filters: DepartmentFilters = {}) => {
  return useQuery({
    queryKey: [DEPARTMENTS_QUERY_KEY, filters],
    queryFn: async (): Promise<PaginatedDepartments> => {
      const response = await apiClient.getDepartments(filters)
      // Приводим ответ API к нужному формату
      return {
        data: response.data || [],
        total: response.total || 0,
        page: response.page || 1,
        limit: response.limit || 20,
        totalPages: response.totalPages || 1,
        from1cCount: response.from1cCount || 0,
        localCount: response.localCount || 0,
      }
    },
    staleTime: 30 * 1000, // 30 seconds
    retry: 3,
  })
}

export const useDepartment = (id: string) => {
  return useQuery({
    queryKey: [DEPARTMENTS_QUERY_KEY, id],
    queryFn: async (): Promise<Department> => {
      const response = await apiClient.getDepartment(id)
      return response.data
    },
    enabled: !!id,
    staleTime: 30 * 1000,
  })
}

export const useCreateDepartment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: Partial<Department>) => {
      const response = await apiClient.createDepartment(data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DEPARTMENTS_QUERY_KEY] })
    },
  })
}

export const useUpdateDepartment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Department> }) => {
      const response = await apiClient.updateDepartment(id, data)
      return response.data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: [DEPARTMENTS_QUERY_KEY] })
      queryClient.invalidateQueries({ queryKey: [DEPARTMENTS_QUERY_KEY, id] })
    },
  })
}

export const useDeleteDepartment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.deleteDepartment(id)
      // Для DELETE операций response может быть null (код 204)
      return response?.data || response
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [DEPARTMENTS_QUERY_KEY] })
    },
  })
}

/**
 * Универсальный хук для получения списка подразделений в селекторах
 * с опциональной фильтрацией по организации
 */
export const useDepartmentsForSelect = (organizationId?: string | null) => {
  return useQuery({
    queryKey: [DEPARTMENTS_QUERY_KEY, 'select', organizationId],
    queryFn: async () => {
      const filters: DepartmentFilters = {
        limit: 100,
        organization_id: organizationId || undefined,
      }
      const response = await apiClient.getDepartments(filters)
      const data = response.data || []
      return data.map((dept: Department) => ({
        id: dept.id,
        name: dept.name,
        organization_id: dept.organization_id,
        employee_count: dept.employee_count ?? 0,
      }))
    },
    staleTime: 5 * 60 * 1000, // 5 минут - подразделения меняются редко
  })
}